import { useState, useEffect } from 'react';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';

const API = 'https://golf-reservation-backend.onrender.com';

// ── ReservationCard를 외부에 정의 (iOS 키보드 버그 방지) ──
function ReservationCard({ r, selectedId, setSelectedId, confirmForm, setConfirmForm, loading, handleConfirm, handleReject, handleDelete }) {
  const statusLabel = (status) => {
    if (status === 'pending') return { text: '검토 중', color: '#f59e0b', bg: '#fef3c7' };
    if (status === 'confirmed') return { text: '예약 확정', color: '#059669', bg: '#d1fae5' };
    if (status === 'rejected') return { text: '예약 불가', color: '#dc2626', bg: '#fee2e2' };
    return { text: status, color: '#888', bg: '#f3f4f6' };
  };
  const s = statusLabel(r.status);
  const isSelected = selectedId === r.id;
  return (
    <div style={cardStyles.card}>
      <div style={cardStyles.cardHeader}>
        <div>
          <p style={cardStyles.requesterName}>{r.requester?.name}</p>
          <p style={cardStyles.clubName}>{r.membership?.club_name} · {r.membership?.member_type}</p>
          {r.is_proxy && <p style={cardStyles.proxyInfo}>대리예약 · {r.actual_user_name}</p>}
        </div>
        <span style={{ ...cardStyles.statusBadge, color: s.color, backgroundColor: s.bg }}>{s.text}</span>
      </div>
      <div style={cardStyles.infoRow}>
        <span style={cardStyles.infoLabel}>희망일</span>
        <span style={cardStyles.infoValue}>
          {dayjs(r.desired_date).format('YYYY년 MM월 DD일')} {r.desired_time || '(시간 확인 요청)'}
        </span>
      </div>
      {r.companions && (
        <div style={cardStyles.infoRow}>
          <span style={cardStyles.infoLabel}>동반자</span>
          <span style={cardStyles.infoValue}>{r.companions}</span>
        </div>
      )}
      {r.note && (
        <div style={cardStyles.infoRow}>
          <span style={cardStyles.infoLabel}>메모</span>
          <span style={cardStyles.infoValue}>{r.note}</span>
        </div>
      )}
      {r.status === 'confirmed' && r.confirmed_date && (
        <div style={cardStyles.confirmedBox}>
          확정일: {dayjs(r.confirmed_date).format('YYYY년 MM월 DD일')} {r.confirmed_time || ''}
        </div>
      )}
      {r.secretary_direct && <span style={cardStyles.directBadge}>비서 직접 예약</span>}
      <p style={cardStyles.dateText}>요청일: {r.created_at?.slice(0, 10)}</p>

      {r.status === 'confirmed' && (
        <button style={cardStyles.deleteBtn} onClick={() => handleDelete(r.id)}>예약 삭제</button>
      )}

      {r.status === 'pending' && (
        <div>
          {!isSelected ? (
            <div style={cardStyles.actionRow}>
              <button style={cardStyles.rejectBtn} onClick={() => handleReject(r.id)}>거절</button>
              <button style={cardStyles.confirmBtn} onClick={() => {
                setSelectedId(r.id);
                setConfirmForm({ confirmed_date: r.desired_date, confirmed_time: r.desired_time || '', note: '' });
              }}>예약 확정하기</button>
            </div>
          ) : (
            <div style={cardStyles.confirmForm}>
              <p style={cardStyles.confirmFormTitle}>확정 정보 입력</p>
              <label style={cardStyles.label}>확정 날짜 *</label>
              <input style={cardStyles.input} type="date"
                value={confirmForm.confirmed_date}
                onChange={e => setConfirmForm({ ...confirmForm, confirmed_date: e.target.value })}
              />
              <label style={cardStyles.label}>확정 티타임</label>
              <input style={cardStyles.input} type="time"
                value={confirmForm.confirmed_time}
                onChange={e => setConfirmForm({ ...confirmForm, confirmed_time: e.target.value })}
              />
              <label style={cardStyles.label}>전달 메모</label>
              <input style={cardStyles.input} type="text"
                placeholder="임원님께 전달할 내용"
                value={confirmForm.note}
                onChange={e => setConfirmForm({ ...confirmForm, note: e.target.value })}
              />
              <div style={cardStyles.actionRow}>
                <button style={cardStyles.rejectBtn} onClick={() => setSelectedId(null)}>취소</button>
                <button style={cardStyles.confirmBtn} onClick={() => handleConfirm(r.id)} disabled={loading}>
                  {loading ? '처리 중...' : '확정 완료'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SecretaryPage({ onLogout }) {
  const user = JSON.parse(localStorage.getItem('golf_user'));
  const [tab, setTab] = useState('pending');
  const [reservations, setReservations] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [allMemberships, setAllMemberships] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [membershipRules, setMembershipRules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDirectForm, setShowDirectForm] = useState(false);

  const [confirmForm, setConfirmForm] = useState({
    confirmed_date: '', confirmed_time: '', note: '',
  });

  const [directForm, setDirectForm] = useState({
    requester_id: '', membership_id: '',
    confirmed_date: '', confirmed_time: '',
    companions: '', note: '',
    is_proxy: false, actual_user_name: '',
  });

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

const fetchAll = async () => {
  try {
    const [r, u, m, allMem, rules] = await Promise.all([
      axios.get(`${API}/reservations`),
      axios.get(`${API}/users`),
      axios.get(`${API}/memberships`),
      axios.get(`${API}/memberships`),
      axios.get(`${API}/membership-rules`),
    ]);
    setReservations(r.data);
    setExecutives(u.data.filter(u => u.role === 'executive'));
    setMemberships(m.data);
    setAllMemberships(allMem.data);
    setMembershipRules(rules.data);
  } catch (e) { console.log(e); }
};

const checkLimit = (reservation, confirmed_date) => {
  const date = new Date(confirmed_date);
  const year = date.getFullYear();
  const month = date.getMonth();
  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
  const dayType = isWeekend ? '주말' : '주중';

  const selectedMembership = allMemberships.find(m => m.id === reservation.membership_id);
  if (!selectedMembership) return true;

  const sameClubMembershipIds = allMemberships
    .filter(m => m.club_name === selectedMembership.club_name)
    .map(m => m.id);

  const monthReservations = reservations.filter(r => {
    if (r.requester_id !== reservation.requester_id) return false;
    if (!sameClubMembershipIds.includes(r.membership_id)) return false;
    if (r.status === 'rejected') return false;
    if (r.id === reservation.id) return false;
    const d = new Date(r.confirmed_date || r.desired_date);
    return d.getFullYear() === year && d.getMonth() === month;
  });

  const weekendCount = monthReservations.filter(r => {
    const d = new Date(r.confirmed_date || r.desired_date);
    return d.getDay() === 0 || d.getDay() === 6;
  }).length;

  const weekdayCount = monthReservations.filter(r => {
    const d = new Date(r.confirmed_date || r.desired_date);
    return d.getDay() !== 0 && d.getDay() !== 6;
  }).length;

  const rule = membershipRules.find(r => r.membership_id === reservation.membership_id);
  if (!rule) return true;

  const limit = isWeekend ? rule.weekend_limit : rule.weekday_limit;
  const used = isWeekend ? weekendCount : weekdayCount;

  if (used >= limit) {
    return window.confirm(
      `⚠️ ${reservation.requester?.name}님의 이번 달 ${selectedMembership.club_name} ${dayType} 예약 한도(${limit}회)를 초과했어요.\n현재 ${used}회 사용 중\n\n그래도 확정하시겠어요?`
    );
  }
  return true;
};

  const handleConfirm = async (id) => {
    if (!confirmForm.confirmed_date) { alert('확정 날짜를 입력해주세요!'); return; }
    setLoading(true);

    const reservation = reservations.find(r => r.id === id);
    const canProceed = checkLimit(reservation, confirmForm.confirmed_date);
    if (!canProceed) { setLoading(false); return; }

    try {
      await axios.patch(`${API}/reservations/${id}`, {
        status: 'confirmed',
        confirmed_date: confirmForm.confirmed_date,
        confirmed_time: confirmForm.confirmed_time,
        note: confirmForm.note,
      });
      alert('예약이 확정됐어요! 임원님께 텔레그램 알림이 전송됐습니다.');
      setSelectedId(null);
      setConfirmForm({ confirmed_date: '', confirmed_time: '', note: '' });
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || '처리 실패. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('이 예약을 삭제하시겠어요? 삭제 후 복구할 수 없습니다.')) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/reservations/${id}`);
      alert('예약이 삭제됐어요.');
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || '삭제 실패. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const handleReject = async (id) => {
    const reason = window.prompt('거절 사유를 입력해주세요:');
    if (reason === null) return;
    setLoading(true);
    try {
      await axios.patch(`${API}/reservations/${id}`, { status: 'rejected', note: reason });
      alert('거절 처리됐어요. 임원님께 알림이 전송됐습니다.');
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || '처리 실패. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const handleDirectReservation = async () => {
    if (!directForm.requester_id || !directForm.membership_id || !directForm.confirmed_date) {
      alert('임원, 회원권, 확정 날짜는 필수예요!'); return;
    }
    if (directForm.is_proxy && !directForm.actual_user_name) {
      alert('실제 사용자 이름을 입력해주세요!'); return;
    }

    const fakeReservation = {
      requester_id: directForm.requester_id,
      membership_id: directForm.membership_id,
      requester: executives.find(e => e.id === directForm.requester_id),
    };
    const canProceed = checkLimit(fakeReservation, directForm.confirmed_date);
    if (!canProceed) return;

    setLoading(true);
    try {
      await axios.post(`${API}/reservations/secretary`, {
        ...directForm,
        secretary_id: user.id,
      });
      alert('예약이 등록됐어요! 임원님께 텔레그램 알림이 전송됐습니다.');
      setShowDirectForm(false);
      setDirectForm({
        requester_id: '', membership_id: '',
        confirmed_date: '', confirmed_time: '',
        companions: '', note: '',
        is_proxy: false, actual_user_name: '',
      });
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.error || '등록 실패. 다시 시도해주세요.');
    }
    setLoading(false);
  };

  const statusLabel = (status) => {
    if (status === 'pending') return { text: '검토 중', color: '#f59e0b', bg: '#fef3c7' };
    if (status === 'confirmed') return { text: '예약 확정', color: '#059669', bg: '#d1fae5' };
    if (status === 'rejected') return { text: '예약 불가', color: '#dc2626', bg: '#fee2e2' };
  };

  const getReservationsForDate = (date) => {
    const dateStr = date.toLocaleDateString('sv-SE');
    return reservations.filter(r => (r.confirmed_date || r.desired_date) === dateStr);
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const list = getReservationsForDate(date);
    if (list.length === 0) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginTop: '2px', flexWrap: 'wrap' }}>
        {list.slice(0, 3).map((r, i) => {
          const s = statusLabel(r.status);
          return <div key={i} style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: s.color }} />;
        })}
      </div>
    );
  };

  // 이 비서에게 배정된 임원 ID 목록
  const myExecutiveIds = executives
    .filter(e => e.assigned_secretary_id === user.id)
    .map(e => e.id);

  // 본인 담당 임원의 예약만 표시
  const myReservations = reservations.filter(r => myExecutiveIds.includes(r.requester_id));

  const filtered = myReservations.filter(r => r.status === tab);
  const pendingCount = myReservations.filter(r => r.status === 'pending').length;
  const selectedDateReservations = selectedDate ? getReservationsForDate(selectedDate) : [];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <p style={styles.headerSub}>비서 관리 화면</p>
          <p style={styles.headerName}>{user?.name}님 ⛳</p>
        </div>
        <button style={styles.logoutBtn} onClick={onLogout}>로그아웃</button>
      </div>

      <div style={styles.content}>
        {pendingCount > 0 && (
          <div style={styles.alertBox}>
            처리 대기 중인 예약 요청이 {pendingCount}건 있어요
          </div>
        )}

        {/* 직접 예약 등록 */}
        {!showDirectForm ? (
          <button style={styles.directBtn} onClick={() => setShowDirectForm(true)}>
            + 직접 예약 등록
          </button>
        ) : (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>직접 예약 등록</h2>
            <p style={styles.infoText}>골프장 예약 완료 후 시스템에 등록하고 임원님께 알림을 보내요.</p>

            <label style={styles.label}>임원 *</label>
            <select style={styles.input} value={directForm.requester_id}
              onChange={e => setDirectForm({ ...directForm, requester_id: e.target.value })}
            >
              <option value="">선택해주세요</option>
              {executives.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>

            <label style={styles.label}>회원권 *</label>
            <select style={styles.input} value={directForm.membership_id}
              onChange={e => setDirectForm({ ...directForm, membership_id: e.target.value })}
            >
              <option value="">선택해주세요</option>
              {memberships.map(m => (
                <option key={m.id} value={m.id}>{m.club_name} · {m.member_type}</option>
              ))}
            </select>

            <label style={styles.label}>확정 날짜 *</label>
            <input style={styles.input} type="date"
              min={dayjs().format('YYYY-MM-DD')}
              value={directForm.confirmed_date}
              onChange={e => setDirectForm({ ...directForm, confirmed_date: e.target.value })}
            />

            <label style={styles.label}>확정 티타임</label>
            <input style={styles.input} type="time"
              value={directForm.confirmed_time}
              onChange={e => setDirectForm({ ...directForm, confirmed_time: e.target.value })}
            />

            <label style={styles.label}>동반자</label>
            <input style={styles.input} type="text"
              placeholder="예: 김철수 상무, 이영희 전무"
              value={directForm.companions}
              onChange={e => setDirectForm({ ...directForm, companions: e.target.value })}
            />

            <div style={styles.checkRow}>
              <label style={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={directForm.is_proxy}
                  onChange={e => setDirectForm({ ...directForm, is_proxy: e.target.checked, actual_user_name: '' })}
                  style={{ marginRight: '8px' }}
                />
                위임/무기명 예약 (실제 사용자가 다른 경우)
              </label>
            </div>

            {directForm.is_proxy && (
              <div>
                <label style={styles.label}>실제 사용자 이름 *</label>
                <input style={styles.input} type="text"
                  placeholder="실제 골프 치시는 분 성함"
                  value={directForm.actual_user_name}
                  onChange={e => setDirectForm({ ...directForm, actual_user_name: e.target.value })}
                />
              </div>
            )}

            <label style={styles.label}>메모</label>
            <textarea
              style={{ ...styles.input, height: '80px', resize: 'none' }}
              placeholder="특이사항이 있으면 입력해주세요"
              value={directForm.note}
              onChange={e => setDirectForm({ ...directForm, note: e.target.value })}
            />

            <div style={styles.actionRow}>
              <button style={styles.rejectBtn} onClick={() => setShowDirectForm(false)}>취소</button>
              <button style={styles.confirmBtn} onClick={handleDirectReservation} disabled={loading}>
                {loading ? '등록 중...' : '예약 등록 + 알림 발송'}
              </button>
            </div>
          </div>
        )}

        {/* 탭 */}
        <div style={styles.tabRow}>
          {[
            { key: 'pending', label: '대기 중' },
            { key: 'confirmed', label: '확정' },
            { key: 'rejected', label: '거절' },
            { key: 'calendar', label: '달력' },
          ].map(t => (
            <button
              key={t.key}
              style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              {t.key === 'pending' && pendingCount > 0 && (
                <span style={styles.badge}>{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* 달력 탭 */}
        {tab === 'calendar' && (
          <div>
            <div style={styles.card}>
              <style>{`
                .react-calendar { width: 100%; border: none; font-family: inherit; }
                .react-calendar__tile { padding: 10px 6px; font-size: 13px; }
                .react-calendar__tile--active { background: #1e3a5f !important; color: white !important; border-radius: 6px; }
                .react-calendar__tile--now { background: #e0f2fe; border-radius: 6px; }
                .react-calendar__navigation button { font-size: 14px; font-weight: 600; }
              `}</style>
              <Calendar onChange={setSelectedDate} value={selectedDate} tileContent={tileContent} locale="ko-KR" />
            </div>
            {selectedDate ? (
              <div>
                <h2 style={styles.sectionTitle}>
                  {selectedDate.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })} ({selectedDateReservations.length}건)
                </h2>
                {selectedDateReservations.length === 0 ? (
                  <div style={styles.emptyBox}>이 날짜에 예약이 없어요</div>
                ) : (
                  selectedDateReservations.map(r => <ReservationCard key={r.id} r={r} selectedId={selectedId} setSelectedId={setSelectedId} confirmForm={confirmForm} setConfirmForm={setConfirmForm} loading={loading} handleConfirm={handleConfirm} handleReject={handleReject} handleDelete={handleDelete} />)
                )}
              </div>
            ) : (
              <div style={styles.emptyBox}>달력에서 날짜를 선택하면 예약 내역이 표시돼요</div>
            )}
          </div>
        )}

        {/* 예약 목록 탭 */}
        {tab !== 'calendar' && (
          <div>
            {filtered.length === 0 ? (
              <div style={styles.emptyBox}>해당 예약이 없어요</div>
            ) : (
              filtered.map(r => <ReservationCard key={r.id} r={r} selectedId={selectedId} setSelectedId={setSelectedId} confirmForm={confirmForm} setConfirmForm={setConfirmForm} loading={loading} handleConfirm={handleConfirm} handleReject={handleReject} handleDelete={handleDelete} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f4f0' },
  header: {
    backgroundColor: '#1e3a5f', padding: '20px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  headerSub: { color: '#a0b4cc', fontSize: '12px' },
  headerName: { color: 'white', fontSize: '18px', fontWeight: '600' },
  logoutBtn: {
    backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
    color: 'white', padding: '6px 14px', borderRadius: '6px', fontSize: '13px',
  },
  content: { padding: '20px 16px', maxWidth: '600px', margin: '0 auto' },
  alertBox: {
    backgroundColor: '#fef3c7', color: '#92400e',
    padding: '12px 16px', borderRadius: '8px',
    marginBottom: '16px', fontSize: '14px', fontWeight: '500',
  },
  directBtn: {
    width: '100%', padding: '14px', backgroundColor: '#1e3a5f',
    color: 'white', border: 'none', borderRadius: '10px',
    fontSize: '15px', fontWeight: '600', marginBottom: '16px',
  },
  tabRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  tab: {
    flex: 1, padding: '10px', backgroundColor: 'white',
    border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '13px', fontWeight: '500', color: '#666', minWidth: '70px',
  },
  tabActive: { backgroundColor: '#1e3a5f', color: 'white', border: '1px solid #1e3a5f' },
  badge: {
    backgroundColor: '#ef4444', color: 'white',
    borderRadius: '10px', padding: '1px 6px',
    fontSize: '11px', marginLeft: '4px',
  },
  sectionTitle: { fontSize: '15px', fontWeight: '600', marginBottom: '12px', marginTop: '8px' },
  emptyBox: {
    backgroundColor: 'white', borderRadius: '10px',
    padding: '32px', textAlign: 'center', color: '#aaa', fontSize: '14px',
  },
  card: {
    backgroundColor: 'white', borderRadius: '12px',
    padding: '16px', marginBottom: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  },
  cardTitle: { fontSize: '16px', fontWeight: '600', marginBottom: '8px' },
  infoText: { fontSize: '13px', color: '#666', marginBottom: '14px' },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: '12px',
  },
  requesterName: { fontSize: '16px', fontWeight: '600', marginBottom: '2px' },
  clubName: { fontSize: '13px', color: '#444', marginBottom: '2px' },
  proxyInfo: { fontSize: '12px', color: '#7c3aed' },
  statusBadge: { fontSize: '12px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px' },
  infoRow: { display: 'flex', gap: '8px', marginBottom: '6px' },
  infoLabel: { fontSize: '12px', color: '#999', minWidth: '40px' },
  infoValue: { fontSize: '13px', color: '#444' },
  confirmedBox: {
    backgroundColor: '#d1fae5', color: '#065f46',
    padding: '8px 12px', borderRadius: '6px',
    fontSize: '13px', fontWeight: '500', marginTop: '8px',
  },
  directBadge: {
    fontSize: '11px', color: '#0369a1', backgroundColor: '#e0f2fe',
    padding: '2px 8px', borderRadius: '10px',
    display: 'inline-block', marginTop: '6px',
  },
  dateText: { fontSize: '11px', color: '#bbb', marginTop: '10px' },
  actionRow: { display: 'flex', gap: '8px', marginTop: '12px' },
  rejectBtn: {
    flex: 1, padding: '10px', backgroundColor: 'white',
    border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '14px', color: '#666',
  },
  confirmBtn: {
    flex: 2, padding: '10px', backgroundColor: '#1e3a5f',
    color: 'white', border: 'none', borderRadius: '8px',
    fontSize: '14px', fontWeight: '600',
  },
  confirmForm: {
    backgroundColor: '#f8fafc', borderRadius: '8px',
    padding: '14px', marginTop: '12px',
  },
  confirmFormTitle: { fontSize: '14px', fontWeight: '600', marginBottom: '10px' },
  label: { fontSize: '12px', fontWeight: '500', color: '#555', display: 'block', marginBottom: '4px', marginTop: '10px' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '1px solid #ddd', borderRadius: '8px',
    fontSize: '14px', outline: 'none', backgroundColor: 'white',
  },
  checkRow: { marginTop: '12px', marginBottom: '4px' },
  checkLabel: { fontSize: '14px', color: '#444', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  deleteBtn: {
    marginTop: '10px', width: '100%', padding: '8px',
    backgroundColor: 'white', border: '1px solid #fca5a5',
    color: '#dc2626', borderRadius: '8px', fontSize: '13px',
  },
};

const cardStyles = styles;